import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PcmpSkippedProposal } from '../../types/pcmpQueue.types';

interface PcmpOutcomeDialogProps {
  title: string;
  summary: string;
  /** Proposals the API left out, each with its reason. */
  skipped: PcmpSkippedProposal[];
  /** Ref numbers for the ids the caller has loaded. */
  refNoOf?: ((proposalId: number) => string | undefined) | undefined;
  actions?: ReactNode;
  onClose: () => void;
}

/** What a bulk batch action did, listing the proposals it had to leave out. */
export function PcmpOutcomeDialog({
  title,
  summary,
  skipped,
  refNoOf,
  actions,
  onClose,
}: PcmpOutcomeDialogProps) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{summary}</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-col gap-2 overflow-y-auto">
          <p className="flex items-center gap-2 text-sm font-medium">
            <TriangleAlert className="size-4 text-warning-foreground" aria-hidden />
            Left out ({skipped.length})
          </p>
          <ul className="flex flex-col divide-y rounded-lg border text-sm">
            {skipped.map((item) => (
              <li key={item.proposalId} className="flex flex-col gap-0.5 px-3 py-2">
                <Link
                  to="/pcmp/proposals/$proposalId"
                  params={{ proposalId: item.proposalId }}
                  target="_blank"
                  className="w-fit font-mono font-medium text-primary hover:underline"
                >
                  {refNoOf?.(item.proposalId) ?? `Proposal #${item.proposalId}`}
                </Link>
                <span className="text-muted-foreground">{item.reason}</span>
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter>
          {actions}
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
